import Grid from '@mui/material/Grid';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  changeFilterTenderGroupBy,
  findReportTenderRequested,
  reportFilterAppliedSelector,
  reportFilterTenderGroupBySelector,
  reportTenderLoadingSelector,
  reportFilterStartBusinessDateSelector,
  reportFilterEndBusinessDateSelector,
  reportFilterStartAccountingDateSelector,
  reportFilterEndAccountingDateSelector,
  reportFilterStartOperationDateSelector,
  reportFilterEndOperationDateSelector,
  reportFilterCountryCodeSelector,
} from '../state';

import {
  AccountingDateRangeFilter,
  BusinessDateRangeFilter,
  CountryFilter,
  HourRangeFilter,
  OperationDateRangeFilter,
  OperationTypeFilter,
  PaymentTypesFilter,
  PosFilter,
  ReportActionsGridItem,
  ReportFilterActions,
  ReportFilters,
  ReportFiltersProps,
  SalesTypesFilter,
  SegmentsFilter,
  StoreFilter,
  TimeBandFilter,
  OperatorDlvFilter,
} from './ReportFilters';

import { AppPermissions, ReportFilterTenderGroupBy } from '../types';
import { PermissionsProvider } from './Permissions';
import { constants } from '../helpers';
import { FilterPanel, StatusChip } from './Visual';

const getGroupByConfig = (
  isBrazil: boolean
): ReportFiltersProps<ReportFilterTenderGroupBy>['groupByConfig'] => ({
  options: {
    countryAcronym: true,
    tenderName: false,
    storeAcronym: false,
    operatorDlv: false,
    businessDate: false,
    accountingDate: false,
    operationDate: false,
    posNum: false,
    saleType: false,
    timeBand: false,
    timeRange: false,
    posType: false,
    operationTypeName: false,
    ...(isBrazil ? { installmentQuantity: false, acquirerCnpj: false } : {}),
    regionalMgmtId: false,
    regionId: false,
    mgmtId: false,
    supervisionId: false,
    agreementId: false,
    companyId: false,
  } as Record<ReportFilterTenderGroupBy, boolean>,
  action: changeFilterTenderGroupBy,
  selector: reportFilterTenderGroupBySelector,
});

const dialogFiltersConfig: ReportFiltersProps<ReportFilterTenderGroupBy>['dialogFiltersConfig'] = {
  options: [
    'segments',
    'salesTypes',
    'pos',
    'hourRange',
    'operationType',
    'startOperationDate',
    'endOperationDate',
    'startAccountingDate',
    'endAccountingDate',
  ],
  selector: reportFilterAppliedSelector,
  filters: (
    <>
      <OperatorDlvFilter />
      <SegmentsFilter />
      <SalesTypesFilter />
      <PosFilter />
      <HourRangeFilter />
      <TimeBandFilter />
      <OperationTypeFilter />
      <OperationDateRangeFilter maxDaysBetween={constants.reportsFilterLimitDateRangeThirtyOne} />
      <AccountingDateRangeFilter maxDaysBetween={constants.reportsFilterLimitDateRangeThirtyOne} />
    </>
  ),
};

const onedriveAction = () => findReportTenderRequested(true);

const SalesTotalsSearch = () => {
  const { t } = useTranslation();
  const startBusinessDate = useSelector(reportFilterStartBusinessDateSelector);
  const endBusinessDate = useSelector(reportFilterEndBusinessDateSelector);
  const startAccountingDate = useSelector(reportFilterStartAccountingDateSelector);
  const endAccountingDate = useSelector(reportFilterEndAccountingDateSelector);
  const startOperationDate = useSelector(reportFilterStartOperationDateSelector);
  const endOperationDate = useSelector(reportFilterEndOperationDateSelector);
  const countryCode = useSelector(reportFilterCountryCodeSelector);
  const hasCountry = Boolean(countryCode);
  const groupByConfig = useMemo(() => getGroupByConfig(countryCode === 'BR'), [countryCode]);
  const disableAdvancedActions = constants.isSmsReportedCountry(countryCode);
  const hasAnyDateRange =
    (startBusinessDate.accept && endBusinessDate.accept) ||
    (startAccountingDate.accept && endAccountingDate.accept) ||
    (startOperationDate.accept && endOperationDate.accept);
  const applyDisabled = !(hasAnyDateRange && hasCountry);
  const countryRequiredIncomplete = applyDisabled && !hasCountry;
  const dateRangeRequiredIncomplete = applyDisabled && !hasAnyDateRange;

  return (
    <PermissionsProvider resourcePermissions={[AppPermissions.TENDER]}>
      <ReportFilters
        groupByConfig={groupByConfig}
        dialogFiltersConfig={dialogFiltersConfig}
        loadingSelector={reportTenderLoadingSelector}
        applyAction={findReportTenderRequested}
        onedriveAction={onedriveAction}
        showOpStructureFilters
        disableAdvancedActions={disableAdvancedActions}
      >
        <FilterPanel
          subtitleTranslationKey="report-tender.filters.subtitle"
          actions={
            <StatusChip
              status={applyDisabled ? 'warning' : 'success'}
              label={
                applyDisabled ? t('filters.status.missingRequired') : t('filters.status.ready')
              }
            />
          }
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <CountryFilter fullWidth requiredIncomplete={countryRequiredIncomplete} />
            </Grid>
            <Grid item xs={12} md={4} lg={3} xl={2}>
              <StoreFilter />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <PaymentTypesFilter />
            </Grid>
            <Grid item xs={12} md={6} lg={5} xl={3}>
              <BusinessDateRangeFilter
                spacing={1}
                maxDaysBetween={constants.reportsFilterLimitDateRangeThirtyOne}
                requiredIncomplete={dateRangeRequiredIncomplete}
              />
            </Grid>
            <ReportActionsGridItem>
              <ReportFilterActions applyDisabled={applyDisabled} />
            </ReportActionsGridItem>
          </Grid>
        </FilterPanel>
      </ReportFilters>
    </PermissionsProvider>
  );
};

export default SalesTotalsSearch;
